/**
 * Spec 256 — the engine strip.
 *
 * Five engine cells in one hairline row at the top of Overview and Collection:
 * the engine's name, a status dot and word (Live, Sample, Failing, Off), and
 * when it last ran. It always shows — the state of collection is glanceable,
 * not only alarming.
 *
 * It absorbed the degradation banner (spec 134; fixes from 220 and 253): a
 * Failing or Off cell carries the one readable line, its fix (Top up credits,
 * or Turn back on for orbit:admin), and the provider's raw reply behind a
 * disclosure. The tone rides a 3px top edge and the dot, never a filled block.
 */
import { useState } from 'react';
import type { EngineDegradation } from '../types/index.js';
import { useAuth } from '../lib/auth-context.js';
import { apiFetch } from '../lib/api.js';
import {
  STRIP_ENGINES,
  describeProblem,
  engineName,
  engineRow,
  engineStatus,
  relativeTime,
} from '../lib/engine-health.js';
import type { EngineStatus } from '../lib/engine-health.js';

export interface EngineStripProps {
  degradation?: EngineDegradation | null;
  /** Each engine's own latest executed query (ISO). Undefined while unknown. */
  lastByEngine?: Record<string, string | null>;
  /** True until the degradation payload has arrived. */
  loading?: boolean;
  /** The payload could not be read. Without this a failed fetch left every
   *  cell on "Checking" for good — a quiet state that looks like progress. */
  unavailable?: boolean;
  onRetry?: () => void;
}

const STATUS_WORD: Record<EngineStatus, string> = {
  live: 'Live',
  sample: 'Sample',
  failing: 'Failing',
  off: 'Off',
};

type ReenableState = 'working' | 'done' | 'failed';

export function EngineStrip({ degradation, lastByEngine, loading, unavailable, onRetry }: EngineStripProps) {
  const { hasRole } = useAuth();
  // Re-enabling a provider is an orbit:admin route (routes/admin-providers.ts).
  const canReenable = hasRole('orbit:admin');
  const [reenabled, setReenabled] = useState<Record<string, ReenableState>>({});

  const reenable = async (engine: string) => {
    setReenabled((s) => ({ ...s, [engine]: 'working' }));
    try {
      const res = await apiFetch(`/api/v1/admin/providers/${encodeURIComponent(engine)}/reenable`, { method: 'POST' });
      setReenabled((s) => ({ ...s, [engine]: res.ok ? 'done' : 'failed' }));
    } catch {
      setReenabled((s) => ({ ...s, [engine]: 'failed' }));
    }
  };

  return (
    <section className="hub-engine-strip" aria-label="AI engines" aria-busy={(loading && !unavailable) || undefined} data-testid="engine-strip">
      {unavailable ? (
        <p className="hub-engine-strip__unavailable" data-testid="engine-strip-unavailable">
          Engine status could not be read.{' '}
          {onRetry ? <button type="button" className="hub-btn hub-btn--text" onClick={onRetry}>Retry</button> : null}
        </p>
      ) : null}
      <ul className="hub-engine-strip__list">
        {STRIP_ENGINES.map((engine) => {
          const state = reenabled[engine];
          const derived = engineStatus(engine, degradation);
          // A switch turned back on here is no longer Off; it collects from the next run.
          const status: EngineStatus =
            derived === 'off' && state === 'done'
              ? (degradation?.mock_engines ?? []).includes(engine) ? 'sample' : 'live'
              : derived;
          const row = derived === 'failing' || derived === 'off' ? engineRow(engine, degradation) : undefined;
          const problem = row ? describeProblem(row) : null;
          const last = lastByEngine?.[engine] ?? null;

          return (
            <li
              key={engine}
              className="hub-engine-cell"
              data-testid={`engine-cell-${engine}`}
              data-status={unavailable ? 'unknown' : loading ? undefined : status}
            >
              <span className="hub-engine-cell__name">{engineName(engine)}</span>
              <span className="hub-engine-cell__status">
                <span className="hub-engine-cell__dot" aria-hidden="true" />
                {unavailable ? 'Unknown' : loading ? 'Checking' : STATUS_WORD[status]}
              </span>
              {lastByEngine !== undefined && (
                <span className="hub-engine-cell__meta">
                  {last ? (
                    <>
                      Last ran <time dateTime={last} title={new Date(last).toLocaleString()}>{relativeTime(last)}</time>
                    </>
                  ) : (
                    'Not run yet'
                  )}
                </span>
              )}
              {status === 'sample' && (
                <span className="hub-engine-cell__meta">Sample (mock) data — no live API key</span>
              )}

              {problem && (
                <div className="hub-engine-cell__problem" data-testid={`engine-problem-${engine}`}>
                  <p className="hub-engine-cell__text" data-testid={`engine-line-${engine}`}>
                    {state === 'done'
                      ? `${engineName(engine)} is turned back on — it collects again from the next run.`
                      : problem.text}
                  </p>
                  {problem.fix?.kind === 'reenable' && state !== 'done' && (
                    canReenable ? (
                      <div className="hub-engine-cell__fix">
                        <button
                          type="button"
                          className="hub-btn hub-btn--ghost"
                          data-testid={`engine-reenable-${engine}`}
                          disabled={state === 'working'}
                          onClick={() => void reenable(engine)}
                        >
                          {state === 'working' ? 'Turning on…' : 'Turn back on'}
                        </button>
                        {state === 'failed' && (
                          <span className="hub-engine-cell__meta" role="status">
                            That didn't work — try again, or use Feature flags.
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="hub-engine-cell__meta">An admin can turn it back on.</span>
                    )
                  )}
                  {problem.fix?.kind === 'link' && (
                    <a
                      className="hub-engine-cell__link"
                      href={problem.fix.href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`engine-fix-${engine}`}
                    >
                      {problem.fix.label}
                    </a>
                  )}
                  {problem.raw && (
                    <details className="hub-engine-cell__raw">
                      <summary>Provider's reply</summary>
                      <code className="hub-code">{problem.raw}</code>
                    </details>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
